import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { canAccess } from "./utils/roles";

import Login from "./pages/Login";
import ResetPassword from "./pages/ResetPassword";
import Dashboard from "./pages/Dashboard";
import LetterheadTemplates from "./pages/letterhead/LetterheadTemplates";
import LetterheadEditor from "./pages/letterhead/LetterheadEditor";
import LetterheadApprovals from "./pages/letterhead/LetterheadApprovals";
import LetterheadHistory from "./pages/letterhead/LetterheadHistory";
import TravelAdvanceRequest from "./pages/expense/TravelAdvanceRequest";
import ExpenseClaimSubmission from "./pages/expense/ExpenseClaimSubmission";
import ExpenseApprovals from "./pages/expense/ExpenseApprovals";
import ExpenseFinanceReview from "./pages/expense/ExpenseFinanceReview";
import ExpenseReports from "./pages/expense/ExpenseReports";
import VendorMaster from "./pages/vendor/VendorMaster";
import ProjectCodes from "./pages/vendor/ProjectCodes";
import PaymentRequests from "./pages/vendor/PaymentRequests";
import VendorInvoiceUpload from "./pages/vendor/VendorInvoiceUpload";
import GRNVerification from "./pages/vendor/GRNVerification";
import PaymentApprovals from "./pages/vendor/PaymentApprovals";
import FinanceProcessing from "./pages/vendor/FinanceProcessing";
import PaymentReleased from "./pages/vendor/PaymentReleased";
import PaymentReports from "./pages/vendor/PaymentReports";
import VendorApprovals from "./pages/vendor/VendorApprovals";
import UserManagement from "./pages/admin/UserManagement";
import BusinessCardRequest from "./pages/hr/BusinessCardRequest";
import ComplaintBox from "./pages/complaints/ComplaintBox";

// eTAMS additions
import EtamsOverview from "./pages/etams/EtamsOverview";
import TravelRequests from "./pages/etams/TravelRequests";
import NewTravelRequest from "./pages/etams/NewTravelRequest";
import CreateExpenseClaim from "./pages/etams/CreateExpenseClaim";
import ReceiptCentre from "./pages/etams/ReceiptCentre";
import TravelPolicy from "./pages/etams/TravelPolicy";
// TSE Workspace additions
import WsEmployeeWall from "./pages/workspace/EmployeeWall";
import WsMyProfile from "./pages/workspace/MyProfile";
import WsApprovals from "./pages/workspace/Approvals";
import WsServiceHub from "./pages/workspace/ServiceHub";
import WsPolicyCenter from "./pages/workspace/PolicyCenter";
import WsProjectLetters from "./pages/workspace/ProjectLetters";
import WsProjectCodes from "./pages/workspace/ProjectCodes";
import WsCostCenters from "./pages/workspace/CostCenters";
import WsMemos from "./pages/workspace/Memos";
import WsMyRequests from "./pages/workspace/MyRequests";
import WsITAssetRequest from "./pages/workspace/ITAssetRequest";
import WsDeskRequest from "./pages/workspace/DeskRequest";
import WsEngineering from "./pages/workspace/Engineering";
import WsProcurement from "./pages/workspace/Procurement";
import WsOperations from "./pages/workspace/Operations";
import WsHrAdmin from "./pages/workspace/HrAdmin";
import WsEhs from "./pages/workspace/Ehs";
import WsFinance from "./pages/workspace/Finance";
import WsCashbook from "./pages/workspace/Cashbook";
import WsAdminConsole from "./pages/workspace/AdminConsole";
import WsAdminWorkflows from "./pages/workspace/AdminWorkflows";

// Auth guard
function ProtectedRoute({ children }) {
  const { isLoggedIn } = useAuth();
  return isLoggedIn ? children : <Navigate to="/" replace />;
}

// Role guard — redirects to dashboard if role lacks access
function RoleRoute({ navKey, children }) {
  const { isLoggedIn, user } = useAuth();
  if (!isLoggedIn) return <Navigate to="/" replace />;
  if (!canAccess(user, navKey))
    return <Navigate to="/dashboard" replace />;
  return children;
}

// Approval pages: approvals are routed to a specific person (the employee's
// Reporting Manager / HOD / Finance / HR), and that person can hold any role —
// so any signed-in internal user may open these pages. The pages themselves
// only list (and the API only allows) items actually pending with that user.
function ApproverRoute({ children }) {
  const { isLoggedIn, user } = useAuth();
  if (!isLoggedIn) return <Navigate to="/" replace />;
  if (!user || user.role === 'vendor') return <Navigate to="/dashboard" replace />;
  return children;
}

function AccessDenied() {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "80vh",
        gap: 16,
      }}
    >
      <i
        className="bi bi-shield-lock"
        style={{ fontSize: 48, color: "#ef4444" }}
      />
      <h4 style={{ color: "#111827" }}>Access Denied</h4>
      <p style={{ color: "#6b7280" }}>
        You don't have permission to view this page.
      </p>
    </div>
  );
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<Login />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <Dashboard />
          </ProtectedRoute>
        }
      />

      {/* Letterhead */}
      <Route
        path="/letterhead/templates"
        element={
          <RoleRoute navKey="letterhead.templates">
            <LetterheadTemplates />
          </RoleRoute>
        }
      />
      <Route
        path="/letterhead/editor"
        element={
          <RoleRoute navKey="letterhead.editor">
            <LetterheadEditor />
          </RoleRoute>
        }
      />
      <Route
        path="/letterhead/approvals"
        element={
          <ApproverRoute>
            <LetterheadApprovals />
          </ApproverRoute>
        }
      />
      <Route
        path="/letterhead/history"
        element={
          <RoleRoute navKey="letterhead.history">
            <LetterheadHistory />
          </RoleRoute>
        }
      />

      {/* Expense */}
      <Route
        path="/expense/advance-request"
        element={
          <RoleRoute navKey="expense.advance">
            <TravelAdvanceRequest />
          </RoleRoute>
        }
      />
      <Route
        path="/expense/claim-submission"
        element={
          <RoleRoute navKey="expense.claim">
            <ExpenseClaimSubmission />
          </RoleRoute>
        }
      />
      <Route
        path="/expense/approvals"
        element={
          <ApproverRoute>
            <ExpenseApprovals />
          </ApproverRoute>
        }
      />
      <Route
        path="/expense/finance-review"
        element={
          <RoleRoute navKey="expense.finance">
            <ExpenseFinanceReview />
          </RoleRoute>
        }
      />
      <Route
        path="/expense/reports"
        element={
          <RoleRoute navKey="expense.reports">
            <ExpenseReports />
          </RoleRoute>
        }
      />

      {/* Vendor */}
      <Route
        path="/vendor/master"
        element={
          <RoleRoute navKey="vendor.master">
            <VendorMaster />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/projects"
        element={
          <RoleRoute navKey="vendor.projects">
            <ProjectCodes />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/payment-requests"
        element={
          <RoleRoute navKey="vendor.payment_requests">
            <PaymentRequests />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/invoice-upload"
        element={
          <RoleRoute navKey="vendor.invoice_upload">
            <VendorInvoiceUpload />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/grn-verification"
        element={
          <RoleRoute navKey="vendor.grn">
            <GRNVerification />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/approvals"
        element={
          <ApproverRoute>
            <PaymentApprovals />
          </ApproverRoute>
        }
      />
      <Route
        path="/vendor/finance"
        element={
          <RoleRoute navKey="vendor.finance">
            <FinanceProcessing />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/payment-released"
        element={
          <RoleRoute navKey="vendor.released">
            <PaymentReleased />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/reports"
        element={
          <RoleRoute navKey="vendor.reports">
            <PaymentReports />
          </RoleRoute>
        }
      />
      <Route
        path="/vendor/vendor-approvals"
        element={
          <ApproverRoute>
            <VendorApprovals />
          </ApproverRoute>
        }
      />

      {/* Business Card */}
      <Route
        path="/business-card/requests"
        element={
          <ApproverRoute>
            <BusinessCardRequest />
          </ApproverRoute>
        }
      />

      {/* Complaint Box */}
      <Route
        path="/complaints"
        element={
          <RoleRoute navKey="complaints.box">
            <ComplaintBox />
          </RoleRoute>
        }
      />

      {/* Admin */}
      <Route
        path="/admin/users"
        element={
          <RoleRoute navKey="admin.users">
            <UserManagement />
          </RoleRoute>
        }
      />

      {/* eTAMS — Travel & Expenses */}
      <Route path="/etams" element={<Navigate to="/etams/overview" replace />} />
      <Route path="/etams/overview" element={<RoleRoute navKey="etams.overview"><EtamsOverview /></RoleRoute>} />
      <Route path="/etams/travel-requests" element={<RoleRoute navKey="etams.travel"><TravelRequests /></RoleRoute>} />
      <Route path="/etams/travel-requests/new" element={<RoleRoute navKey="etams.travel"><NewTravelRequest /></RoleRoute>} />
      <Route path="/etams/claims/new" element={<RoleRoute navKey="expense.claim"><CreateExpenseClaim /></RoleRoute>} />
      <Route path="/etams/receipts" element={<RoleRoute navKey="etams.receipts"><ReceiptCentre /></RoleRoute>} />
      <Route path="/etams/approvals" element={<ApproverRoute><ExpenseApprovals /></ApproverRoute>} />
      <Route path="/etams/policy" element={<RoleRoute navKey="etams.policy"><TravelPolicy /></RoleRoute>} />
      {/* TSE Workspace */}
      <Route path="/employee-wall" element={<RoleRoute navKey="workspace.wall"><WsEmployeeWall /></RoleRoute>} />
      <Route path="/profile" element={<RoleRoute navKey="workspace.profile"><WsMyProfile /></RoleRoute>} />
      <Route path="/approvals" element={<RoleRoute navKey="workspace.approvals"><WsApprovals /></RoleRoute>} />
      <Route path="/service-hub" element={<RoleRoute navKey="workspace.servicehub"><WsServiceHub /></RoleRoute>} />
      <Route path="/policies" element={<RoleRoute navKey="workspace.policies"><WsPolicyCenter /></RoleRoute>} />
      <Route path="/project-admin/letters" element={<RoleRoute navKey="projectadmin.letters"><WsProjectLetters /></RoleRoute>} />
      <Route path="/project-admin/project-codes" element={<RoleRoute navKey="projectadmin.codes"><WsProjectCodes /></RoleRoute>} />
      <Route path="/project-admin/cost-centers" element={<RoleRoute navKey="projectadmin.costcenters"><WsCostCenters /></RoleRoute>} />
      <Route path="/requests/memos" element={<RoleRoute navKey="workspace.requests"><WsMemos /></RoleRoute>} />
      <Route path="/requests/my" element={<RoleRoute navKey="workspace.requests"><WsMyRequests /></RoleRoute>} />
      <Route path="/it-facilities/it-asset" element={<RoleRoute navKey="it.asset"><WsITAssetRequest /></RoleRoute>} />
      <Route path="/it-facilities/desk" element={<RoleRoute navKey="it.desk"><WsDeskRequest /></RoleRoute>} />
      <Route path="/engineering" element={<RoleRoute navKey="engineering.docs"><WsEngineering /></RoleRoute>} />
      <Route path="/procurement" element={<RoleRoute navKey="procurement.stores"><WsProcurement /></RoleRoute>} />
      <Route path="/operations" element={<RoleRoute navKey="operations.site"><WsOperations /></RoleRoute>} />
      <Route path="/hr-admin" element={<RoleRoute navKey="hradmin.services"><WsHrAdmin /></RoleRoute>} />
      <Route path="/ehs" element={<RoleRoute navKey="ehs.compliance"><WsEhs /></RoleRoute>} />
      <Route path="/finance" element={<RoleRoute navKey="finance.control"><WsFinance /></RoleRoute>} />
      <Route path="/cashbook" element={<RoleRoute navKey="finance.cashbook"><WsCashbook /></RoleRoute>} />
      <Route path="/admin/console" element={<RoleRoute navKey="admin.console"><WsAdminConsole /></RoleRoute>} />
      <Route path="/admin/workflows" element={<RoleRoute navKey="admin.workflows"><WsAdminWorkflows /></RoleRoute>} />
      <Route path="/project-admin" element={<Navigate to="/project-admin/letters" replace />} />
      <Route path="/requests" element={<Navigate to="/requests/my" replace />} />
      <Route path="/it-facilities" element={<Navigate to="/it-facilities/it-asset" replace />} />
      <Route path="/admin" element={<Navigate to="/admin/console" replace />} />

      <Route
        path="/access-denied"
        element={
          <ProtectedRoute>
            <AccessDenied />
          </ProtectedRoute>
        }
      />
      <Route path="*" element={<Navigate to="/dashboard" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppRoutes />
      </AuthProvider>
    </BrowserRouter>
  );
}
export default App;
